import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { X, KeyRound } from "lucide-react";
import { API, useApp } from "../context";

const DISMISS_KEY_PREFIX = "iw_set_password_nudge_dismissed_";

/**
 * One-time, dismissible banner for accounts created via Google sign-in or an
 * admin/founder invite that have NO password yet. It points them to the
 * Account → Security panel where they can set a password so they can always
 * log in with email + password (not just Google / a magic link).
 *
 * UX rules:
 *   • Only shows when the authoritative /auth/me reports has_password === false.
 *   • Dismissal is PERSISTENT (localStorage, keyed by user.id) so it truly is
 *     a one-time nudge, not re-shown every session.
 *   • Slim banner, never blocks the page.
 */
const SetPasswordNudge = () => {
  const { user } = useApp();
  const [dismissed, setDismissed] = useState(true);
  const [needsPassword, setNeedsPassword] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const k = DISMISS_KEY_PREFIX + user.id;
    let alreadyDismissed = false;
    try { alreadyDismissed = localStorage.getItem(k) === "1"; } catch (_) {}
    setDismissed(alreadyDismissed);
    if (alreadyDismissed) return;

    // Authoritative check, /auth/me returns has_password (true only when a
    // real bcrypt hash exists). Seed optimistically from the auth payload.
    if (user.has_password === true) { setNeedsPassword(false); return; }
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setNeedsPassword(r.data?.has_password === false);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return null;
  if (dismissed) return null;
  if (!needsPassword) return null;
  // Kids sign in with a username + PIN flow, this nudge is for parent / solo /
  // admin accounts that own an email login.
  if (user.role === "child_explorer") return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY_PREFIX + user.id, "1"); } catch (_) {}
  };

  return (
    <div
      className="rounded-2xl p-3.5 border flex items-center gap-3 text-sm"
      style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(139,92,246,0.08))", borderColor: "rgba(59,130,246,0.30)" }}
      data-testid="set-password-nudge"
    >
      <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-400/30 flex items-center justify-center shrink-0">
        <KeyRound size={16} className="text-blue-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm" data-testid="set-password-nudge-title">
          Add a password to your account
        </p>
        <p className="text-white/60 text-[12px] leading-snug" data-testid="set-password-nudge-message">
          You signed in without a password. Set one so you can always log in with your email, even if Google is unavailable.
        </p>
      </div>
      <Link
        to="/parent-corner?tab=account"
        className="px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-200 text-[11px] font-bold whitespace-nowrap"
        data-testid="set-password-nudge-cta"
      >
        Set a password
      </Link>
      <button
        onClick={dismiss}
        className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white"
        aria-label="Dismiss"
        data-testid="set-password-nudge-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default SetPasswordNudge;
